import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import ForumEntityClassSchema from "notifications/message-notifications/forum-info/forum-entity-class-schema";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import ForumNameSchema from "notifications/message-notifications/forum-info/forum-name-schema";
import { z } from "zod";

export type ForumFilterParametersJSON = z.infer<typeof ForumFilterParametersSchema.ForumFilterParametersJSONZ>;
export type ForumFilterParametersParams = z.infer<typeof ForumFilterParametersSchema.ForumFilterParametersZ>;

export default class ForumFilterParametersSchema {
  public static readonly ForumFilterParametersJSONZ = z.object({
    name: ForumNameSchema.ForumNameJSONZ.nullish(),
    entityId: GuidSchema.GuidJSONZ.nullish(),
    entityClass: ForumEntityClassSchema.ForumEntityClassJSONZ.nullish(),
    context: z.string().nullish(),
    subscriberId: GuidSchema.GuidJSONZ.nullish(),
    minCreatedDate: DateSchema.DateJSONZ.nullish(),
    maxCreatedDate: DateSchema.DateJSONZ.nullish(),
    systemOnly: z.boolean().nullish(),
    forumId: GuidSchema.GuidJSONZ.nullish(),
    query: z.string().nullish(),
    unread: z.boolean().nullish(),
  });
  public static readonly ForumFilterParametersZ = z.object({
    name: z.instanceof(ForumName).optional(),
    entityId: z.instanceof(Guid).optional(),
    entityClass: z.instanceof(ForumEntityClass).optional(),
    context: z.string().optional(),
    subscriberId: z.instanceof(Guid).optional(),
    minCreatedDate: z.instanceof(AHDate).optional(),
    maxCreatedDate: z.instanceof(AHDate).optional(),
    systemOnly: z.instanceof(AHBoolean).optional(),
    forumId: z.instanceof(Guid).optional(),
    query: z.string().optional(),
    unread: z.boolean().optional(),
  });

  public static validateJSON(values: unknown): ForumFilterParametersParams {
    return parseOrThrow(ForumFilterParametersSchema.ForumFilterParametersZ, values, {
      logError: true,
      schemaName: "ForumFilterParametersSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is ForumFilterParametersParams {
    try {
      ForumFilterParametersSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ForumFilterParametersJSON): ForumFilterParametersParams {
    const parsed = parseOrThrow(ForumFilterParametersSchema.ForumFilterParametersJSONZ, data, {
      logError: true,
      schemaName: "ForumFilterParametersSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ForumFilterParametersSchema.codec.decode(parsed);
  }

  public static serialize(filter: ForumFilterParametersParams): ForumFilterParametersJSON {
    const parsed = ForumFilterParametersSchema.validateJSON(filter);
    return ForumFilterParametersSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ForumFilterParametersSchema.ForumFilterParametersJSONZ,
    ForumFilterParametersSchema.ForumFilterParametersZ,
    {
      decode: (data) => ({
        name: ForumName.fromJSON(data.name) ?? undefined,
        entityId: Guid.fromJSON(data.entityId) ?? undefined,
        entityClass: ForumEntityClass.fromJSON(data.entityClass) ?? undefined,
        context: data.context ?? undefined,
        subscriberId: Guid.fromJSON(data.subscriberId) ?? undefined,
        minCreatedDate: AHDate.fromJSON(data.minCreatedDate) ?? undefined,
        maxCreatedDate: AHDate.fromJSON(data.maxCreatedDate) ?? undefined,
        systemOnly: AHBoolean.fromJSON(data.systemOnly) ?? undefined,
        forumId: Guid.fromJSON(data.forumId) ?? undefined,
        query: data.query ?? undefined,
        unread: data.unread ?? undefined,
      }),
      encode: (filter) => ({
        name: filter.name?.toJSON(),
        entityId: filter.entityId?.toJSON(),
        entityClass: filter.entityClass?.toJSON(),
        context: filter.context,
        subscriberId: filter.subscriberId?.toJSON(),
        minCreatedDate: filter.minCreatedDate?.toJSON(),
        maxCreatedDate: filter.maxCreatedDate?.toJSON(),
        systemOnly: filter.systemOnly?.toJSON(),
        forumId: filter.forumId?.toJSON(),
        query: filter.query,
        unread: filter.unread,
      }),
    }
  );
}
